import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useEntry } from '../hooks/useEntry'
import { CandidateWarning } from '../components/CandidateWarning'
import { StatusBadge, POSBadge } from '../components/EvidenceBadges'
import { SuggestionModal } from '../components/SuggestionModal'
import { SiteFooter } from '../components/SiteFooter'

export function EntryPage() {
  const { entryId } = useParams<{ entryId: string }>()
  const { entry, loading, error } = useEntry(entryId)
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <div className="min-h-screen flex flex-col">
      <CandidateWarning />

      <nav className="bg-magar-700 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-white/80 hover:text-white text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Search
          </Link>
          <span className="text-white/40">·</span>
          <span className="text-white/70 text-sm font-mono">{entryId}</span>
        </div>
      </nav>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">

        {loading && (
          <div className="space-y-4 animate-pulse">
            <div className="h-12 w-1/3 bg-stone-200 rounded" />
            <div className="h-6 w-1/4 bg-stone-100 rounded" />
            <div className="h-4 w-1/2 bg-stone-100 rounded" />
          </div>
        )}

        {error && (
          <div className="text-rose-600 bg-rose-50 border border-rose-200 rounded-xl p-4">
            <strong>Could not load entry.</strong> {error}
          </div>
        )}

        {entry && (
          <div className="space-y-6">

            {/* ── Hero: Magar form ─────────────────────────────────── */}
            <section className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 space-y-4">

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={entry.status} />
                <POSBadge pos={entry.pos} />
                {entry.category && entry.category !== entry.pos && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-stone-100 text-stone-600">
                    {entry.category}
                  </span>
                )}
              </div>

              {/* Magar — the hero */}
              <div>
                <p className="deva text-5xl font-bold text-magar-700 leading-tight">
                  {entry.magar_form_devanagari}
                </p>
                {entry.magar_form_romanized && (
                  <p className="text-xl text-magar-600 italic mt-1">
                    {entry.magar_form_romanized}
                  </p>
                )}
                {entry.magar_variant_note && (
                  <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2 border border-amber-100">
                    Also: {entry.magar_variant_note}
                  </p>
                )}
              </div>

              {/* Nepali */}
              <div className="border-t border-stone-100 pt-4 space-y-1">
                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                  Nepali
                </span>
                <div className="flex items-baseline gap-3">
                  <span className="deva text-2xl font-medium text-stone-700">
                    {entry.nepali_form_devanagari}
                  </span>
                  {entry.nepali_form_romanized && (
                    <span className="text-base text-stone-400 italic">
                      {entry.nepali_form_romanized}
                    </span>
                  )}
                </div>
              </div>

              {/* English */}
              {entry.english_gloss && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                    English
                  </span>
                  <p className="text-lg text-stone-500 italic">
                    {entry.english_gloss}
                  </p>
                </div>
              )}
            </section>

            {/* ── Details: dialect & notes ─────────────────────────── */}
            {(entry.dialect || entry.notes) && (
              <section className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 space-y-3">
                <h2 className="text-sm font-semibold text-stone-600 uppercase tracking-wider">
                  Details
                </h2>
                {entry.dialect && (
                  <div>
                    <dt className="text-xs text-stone-400 uppercase tracking-wide">Dialect</dt>
                    <dd className="text-stone-700 mt-0.5">{entry.dialect}</dd>
                  </div>
                )}
                {entry.notes && (
                  <div>
                    <dt className="text-xs text-stone-400 uppercase tracking-wide">Notes</dt>
                    <dd className="text-stone-600 mt-0.5 text-sm leading-relaxed">{entry.notes}</dd>
                  </div>
                )}
              </section>
            )}

            {/* ── Suggest correction ───────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center space-y-3">
              <p className="text-stone-600">
                Is this entry incorrect, incomplete, or missing a dialect variant?
              </p>
              <button
                className="btn-primary text-base px-6 py-3"
                onClick={() => setModalOpen(true)}
              >
                ✏️ Suggest a Correction
              </button>
              <p className="text-xs text-stone-400">
                Suggestions are stored separately and reviewed by the team.
                You cannot overwrite dictionary entries directly.
              </p>
            </div>

          </div>
        )}
      </main>

      {entry && (
        <SuggestionModal
          entry={entry}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}

      <SiteFooter />
    </div>
  )
}
